/**
 * GalleryDetailContext - Shared state provider for all gallery sub-routes.
 *
 * Centralizes:
 * - Gallery data fetching (useGallery, useGalleryImages)
 * - Image statistics (pending, approved, rejected counts)
 * - All mutation actions (approve, reject, flag, include, cover)
 * - Lightbox state
 * - Loading and error states
 *
 * This eliminates prop drilling across Overview, Review, Job, Export pages.
 *
 * @see /src/features/gallery/layouts/GalleryLayout.tsx - Provider wrapper
 * @see /src/features/gallery/hooks/index.ts - Underlying hooks
 */

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'

// Existing hooks
import { useGallery, useApproveGallery, useRejectGallery } from '@/features/gallery/hooks/useGallery'
import { useGalleryImages } from '@/features/gallery/hooks/useGalleryImages'
import {
  useUpdateJobImageStatus,
  useToggleImageInclusion,
  useSetCoverImage,
  useBulkUpdateJobImages,
} from '@/features/gallery/hooks/useGalleryImageMutations'
import { useGalleryEnrichmentDetails } from '@/features/gallery/hooks/useGalleryEnrichmentDetails'
import { useGalleryJobLinkage } from '@/features/gallery/hooks/useGalleryJobLinkage'
import { useSyncGalleryImages } from '@/features/gallery/hooks/useSyncGalleryImages'
import { useJobberJobDetails, type JobberJobDetails } from '@/features/gallery/hooks/useJobberJobDetails'

// Types - Use GalleryAllImages from feature types (matches hooks)
// Import GalleryJobberDetails from shared types as it has additional fields (job_status, job_total, etc.)
import type { GalleryAllImages, BGVGallery } from '../types'
import type { GalleryJobberDetails } from '@/types/gallery'

// Alias for cleaner code
type GalleryImage = GalleryAllImages

/**
 * Statistics for image review states.
 */
export interface ImageStats {
  total: number
  pending: number
  approved: number
  rejected: number
  underReview: number
  included: number
  excluded: number
  jobImages: number
  directPhotos: number
}

/**
 * Lightbox state for viewing images in modal.
 */
export interface LightboxState {
  isOpen: boolean
  currentIndex: number
  images: GalleryImage[]
}

/**
 * Context value interface.
 */
export interface GalleryDetailContextValue {
  // Identity
  galleryId: number | null

  // Data
  gallery: BGVGallery | null
  images: GalleryImage[]
  jobImages: GalleryImage[]
  directPhotos: GalleryImage[]
  enrichment: GalleryJobberDetails | null
  /** Comprehensive job details from jobber_job_details table (synced with images) */
  jobDetails: JobberJobDetails | null
  /** Job linkage data for syncing images (from existing images) */
  jobLinkage: { job_id: string; franchise_region: string; job_number?: number | null } | null

  // Statistics
  stats: ImageStats

  // Loading states
  isLoading: boolean
  isGalleryLoading: boolean
  isImagesLoading: boolean
  isJobDetailsLoading: boolean

  // Error states
  error: Error | null

  // Mutations - Single image
  approveImage: (originalId: number) => void
  rejectImage: (originalId: number) => void
  flagImage: (originalId: number) => void
  toggleInclude: (originalId: number, currentIncluded: boolean) => void
  setCover: (imageUrl: string, jobImageId?: number) => void

  // Mutations - Bulk
  bulkApprove: (originalIds: number[]) => void
  bulkReject: (originalIds: number[]) => void

  // Mutations - Gallery level
  approveGallery: () => void
  rejectGallery: () => void
  syncImages: () => void

  // Mutation states
  isApproving: boolean
  isRejecting: boolean
  isSyncing: boolean

  // Lightbox
  lightbox: LightboxState
  openLightbox: (index: number, images?: GalleryImage[]) => void
  closeLightbox: () => void
  navigateLightbox: (direction: 'next' | 'prev') => void
  setLightboxIndex: (index: number) => void

  // Utilities
  getImageByOriginalId: (originalId: number) => GalleryImage | undefined
  findImageIndex: (originalId: number) => number
  refetch: () => void
}

const GalleryDetailContext = createContext<GalleryDetailContextValue | null>(null)

/**
 * Hook to access gallery detail context.
 * Throws if used outside provider.
 */
export function useGalleryDetail(): GalleryDetailContextValue {
  const context = useContext(GalleryDetailContext)
  if (!context) {
    throw new Error('useGalleryDetail must be used within GalleryDetailProvider')
  }
  return context
}

/**
 * Provider props.
 */
interface GalleryDetailProviderProps {
  children: ReactNode
  /** Override gallery ID (default: from URL params) */
  galleryId?: number
}

/**
 * Provider component that wraps gallery sub-routes.
 */
export function GalleryDetailProvider({
  children,
  galleryId: propGalleryId,
}: GalleryDetailProviderProps) {
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Parse gallery ID from URL or prop
  const parsedId = propGalleryId ?? (id ? parseInt(id, 10) : NaN)
  const galleryId = Number.isNaN(parsedId) ? null : parsedId

  // ─────────────────────────────────────────────────────────────
  // Data fetching
  // ─────────────────────────────────────────────────────────────

  const {
    data: gallery,
    isLoading: isGalleryLoading,
    error: galleryError,
    refetch: refetchGallery,
  } = useGallery(galleryId ?? undefined)

  const {
    data: allImages = [],
    isLoading: isImagesLoading,
    refetch: refetchImages,
  } = useGalleryImages(galleryId ?? 0)

  const { data: enrichment } = useGalleryEnrichmentDetails(galleryId)
  const { data: jobLinkage } = useGalleryJobLinkage(galleryId)
  const { data: jobDetails, isLoading: isJobDetailsLoading } = useJobberJobDetails(
    gallery?.job_number ?? undefined,
    gallery?.franchise_region ?? undefined
  )

  // ─────────────────────────────────────────────────────────────
  // Mutations
  // ─────────────────────────────────────────────────────────────

  const updateJobStatusMutation = useUpdateJobImageStatus(galleryId ?? 0)
  const toggleInclusionMutation = useToggleImageInclusion(galleryId ?? 0)
  const setCoverImageMutation = useSetCoverImage()
  const bulkUpdateMutation = useBulkUpdateJobImages(galleryId ?? 0)
  const approveGalleryMutation = useApproveGallery()
  const rejectGalleryMutation = useRejectGallery()
  const syncImagesMutation = useSyncGalleryImages(galleryId ?? 0)

  // ─────────────────────────────────────────────────────────────
  // Lightbox state
  // ─────────────────────────────────────────────────────────────

  const [lightbox, setLightbox] = useState<LightboxState>({
    isOpen: false,
    currentIndex: 0,
    images: [],
  })

  // ─────────────────────────────────────────────────────────────
  // Computed values
  // ─────────────────────────────────────────────────────────────

  const jobImages = useMemo(
    () => allImages.filter((img) => img.image_type === 'job_image'),
    [allImages]
  )

  const directPhotos = useMemo(
    () => allImages.filter((img) => img.image_type === 'gallery_photo'),
    [allImages]
  )

  const stats = useMemo<ImageStats>(() => {
    const pending = jobImages.filter((img) => img.review_status === 'pending').length
    const approved = jobImages.filter((img) => img.review_status === 'approved').length
    const rejected = jobImages.filter((img) => img.review_status === 'rejected').length
    const underReview = jobImages.filter((img) => img.review_status === 'under_review').length
    // Count images that are "included" - those with a cover status or sort order
    // Note: GalleryAllImages doesn't have explicit is_included, we infer from other fields
    const included = jobImages.filter(
      (img) => img.is_cover ?? img.sort_order !== null
    ).length

    return {
      total: allImages.length,
      pending,
      approved,
      rejected,
      underReview,
      included,
      excluded: jobImages.length - included,
      jobImages: jobImages.length,
      directPhotos: directPhotos.length,
    }
  }, [allImages, jobImages, directPhotos])

  // ─────────────────────────────────────────────────────────────
  // Mutation handlers
  // ─────────────────────────────────────────────────────────────

  /**
   * Approve a job image by its original_id.
   * Looks up franchise_image_job_id for the actual database update.
   *
   * @note original_id is gallery_job_images.id, but status updates need franchise_image_jobs.id
   */
  const approveImage = useCallback(
    (originalId: number) => {
      const image = allImages.find(img => img.original_id === originalId)
      if (!image) {
        toast({ title: 'Image not found', variant: 'destructive' })
        return
      }

      // For job images, use franchise_image_job_id; for gallery photos, use original_id
      const targetId = image.image_type === 'job_image'
        ? image.franchise_image_job_id
        : originalId

      if (!targetId) {
        toast({ title: 'Cannot update', description: 'Missing image ID', variant: 'destructive' })
        return
      }

      updateJobStatusMutation.mutate(
        { jobId: targetId, status: 'approved' },
        {
          onSuccess: () => {
            toast({ title: 'Image approved', variant: 'default' })
          },
          onError: (err) => {
            toast({ title: 'Failed to approve', description: String(err), variant: 'destructive' })
          },
        }
      )
    },
    [allImages, updateJobStatusMutation, toast]
  )

  const rejectImage = useCallback(
    (originalId: number) => {
      const image = allImages.find(img => img.original_id === originalId)
      if (!image) {
        toast({ title: 'Image not found', variant: 'destructive' })
        return
      }

      const targetId = image.image_type === 'job_image'
        ? image.franchise_image_job_id
        : originalId

      if (!targetId) {
        toast({ title: 'Cannot update', description: 'Missing image ID', variant: 'destructive' })
        return
      }

      updateJobStatusMutation.mutate(
        { jobId: targetId, status: 'rejected' },
        {
          onSuccess: () => {
            toast({ title: 'Image rejected', variant: 'default' })
          },
          onError: (err) => {
            toast({ title: 'Failed to reject', description: String(err), variant: 'destructive' })
          },
        }
      )
    },
    [allImages, updateJobStatusMutation, toast]
  )

  const flagImage = useCallback(
    (originalId: number) => {
      const image = allImages.find(img => img.original_id === originalId)
      if (!image) {
        toast({ title: 'Image not found', variant: 'destructive' })
        return
      }

      const targetId = image.image_type === 'job_image'
        ? image.franchise_image_job_id
        : originalId

      if (!targetId) {
        toast({ title: 'Cannot update', description: 'Missing image ID', variant: 'destructive' })
        return
      }

      updateJobStatusMutation.mutate(
        { jobId: targetId, status: 'under_review' },
        {
          onSuccess: () => {
            toast({ title: 'Image flagged for review', variant: 'default' })
          },
          onError: (err) => {
            toast({ title: 'Failed to flag', description: String(err), variant: 'destructive' })
          },
        }
      )
    },
    [allImages, updateJobStatusMutation, toast]
  )

  /**
   * Toggle inclusion of a job image in the gallery.
   * The hook expects `galleryJobImageId` - however, the view exposes `original_id`
   * which is the gallery_job_images.id for job images, so we use that.
   */
  const toggleInclude = useCallback(
    (originalId: number, currentIncluded: boolean) => {
      toggleInclusionMutation.mutate(
        { galleryJobImageId: originalId, isIncluded: !currentIncluded },
        {
          onSuccess: () => {
            toast({
              title: currentIncluded ? 'Image excluded' : 'Image included',
              variant: 'default',
            })
          },
          onError: (err) => {
            toast({ title: 'Failed to update', description: String(err), variant: 'destructive' })
          },
        }
      )
    },
    [toggleInclusionMutation, toast]
  )

  const setCover = useCallback(
    (imageUrl: string, jobImageId?: number) => {
      if (!galleryId) return
      setCoverImageMutation.mutate(
        { id: galleryId, imageUrl, jobImageId },
        {
          onSuccess: () => {
            toast({ title: 'Cover image updated', variant: 'default' })
          },
          onError: (err) => {
            toast({ title: 'Failed to set cover', description: String(err), variant: 'destructive' })
          },
        }
      )
    },
    [galleryId, setCoverImageMutation, toast]
  )

  const bulkApprove = useCallback(
    (originalIds: number[]) => {
      // Map original_ids to franchise_image_job_ids for job images
      const targetIds = originalIds
        .map(id => {
          const image = allImages.find(img => img.original_id === id)
          if (!image) return null
          return image.image_type === 'job_image' ? image.franchise_image_job_id : id
        })
        .filter((id): id is number => id !== null)

      if (targetIds.length === 0) {
        toast({ title: 'No valid images to update', variant: 'destructive' })
        return
      }

      bulkUpdateMutation.mutate(
        { ids: targetIds, status: 'approved' },
        {
          onSuccess: () => {
            toast({ title: `${targetIds.length} images approved`, variant: 'default' })
          },
          onError: (err) => {
            toast({ title: 'Bulk approve failed', description: String(err), variant: 'destructive' })
          },
        }
      )
    },
    [allImages, bulkUpdateMutation, toast]
  )

  const bulkReject = useCallback(
    (originalIds: number[]) => {
      // Map original_ids to franchise_image_job_ids for job images
      const targetIds = originalIds
        .map(id => {
          const image = allImages.find(img => img.original_id === id)
          if (!image) return null
          return image.image_type === 'job_image' ? image.franchise_image_job_id : id
        })
        .filter((id): id is number => id !== null)

      if (targetIds.length === 0) {
        toast({ title: 'No valid images to update', variant: 'destructive' })
        return
      }

      bulkUpdateMutation.mutate(
        { ids: targetIds, status: 'rejected' },
        {
          onSuccess: () => {
            toast({ title: `${targetIds.length} images rejected`, variant: 'default' })
          },
          onError: (err) => {
            toast({ title: 'Bulk reject failed', description: String(err), variant: 'destructive' })
          },
        }
      )
    },
    [allImages, bulkUpdateMutation, toast]
  )

  const approveGallery = useCallback(() => {
    if (!galleryId) return
    approveGalleryMutation.mutate(galleryId, {
      onSuccess: () => {
        toast({ title: 'Gallery approved', variant: 'default' })
      },
      onError: (err) => {
        toast({ title: 'Failed to approve gallery', description: String(err), variant: 'destructive' })
      },
    })
  }, [galleryId, approveGalleryMutation, toast])

  const rejectGallery = useCallback(() => {
    if (!galleryId) return
    rejectGalleryMutation.mutate(galleryId, {
      onSuccess: () => {
        toast({ title: 'Gallery rejected', variant: 'default' })
      },
      onError: (err) => {
        toast({ title: 'Failed to reject gallery', description: String(err), variant: 'destructive' })
      },
    })
  }, [galleryId, rejectGalleryMutation, toast])

  const syncImages = useCallback(() => {
    const jobId = jobLinkage?.job_id ?? enrichment?.job_id
    const franchiseRegion = jobLinkage?.franchise_region ?? enrichment?.franchise_region

    if (!jobId || !franchiseRegion) {
      toast({
        title: 'Cannot sync',
        description: 'No Jobber job linked to this gallery.',
        variant: 'destructive',
      })
      return
    }

    // Map franchise_region to N8N endpoint
    const franchiseMap: Record<string, string> = {
      HQ: 'hamilton',
      hamilton: 'hamilton',
      north_york: 'northyork',
      london: 'hamilton',
      toronto: 'hamilton',
      mississauga: 'hamilton',
      brampton: 'hamilton',
      scarborough: 'northyork',
    }
    const franchise = franchiseMap[franchiseRegion] ?? 'hamilton'

    syncImagesMutation.mutate(
      { jobId, franchise },
      {
        onSuccess: () => {
          toast({ title: 'Images synced from Jobber', variant: 'default' })
        },
        onError: (err) => {
          toast({ title: 'Sync failed', description: String(err), variant: 'destructive' })
        },
      }
    )
  }, [jobLinkage, enrichment, syncImagesMutation, toast])

  // ─────────────────────────────────────────────────────────────
  // Lightbox handlers
  // ─────────────────────────────────────────────────────────────

  const openLightbox = useCallback(
    (index: number, images?: GalleryImage[]) => {
      setLightbox({
        isOpen: true,
        currentIndex: index,
        images: images ?? allImages,
      })
    },
    [allImages]
  )

  const closeLightbox = useCallback(() => {
    setLightbox((prev) => ({ ...prev, isOpen: false }))
  }, [])

  const navigateLightbox = useCallback((direction: 'next' | 'prev') => {
    setLightbox((prev) => {
      const { currentIndex, images } = prev
      if (images.length === 0) return prev

      let newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1
      // Wrap around
      if (newIndex < 0) newIndex = images.length - 1
      if (newIndex >= images.length) newIndex = 0

      return { ...prev, currentIndex: newIndex }
    })
  }, [])

  const setLightboxIndex = useCallback((index: number) => {
    setLightbox((prev) => {
      if (index < 0 || index >= prev.images.length) return prev
      return { ...prev, currentIndex: index }
    })
  }, [])

  // ─────────────────────────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────────────────────────

  const getImageByOriginalId = useCallback(
    (originalId: number) => allImages.find((img) => img.original_id === originalId),
    [allImages]
  )

  const findImageIndex = useCallback(
    (originalId: number) => allImages.findIndex((img) => img.original_id === originalId),
    [allImages]
  )

  const refetch = useCallback(() => {
    refetchGallery()
    refetchImages()
  }, [refetchGallery, refetchImages])

  // ─────────────────────────────────────────────────────────────
  // Context value
  // ─────────────────────────────────────────────────────────────

  const value = useMemo<GalleryDetailContextValue>(
    () => ({
      galleryId,
      gallery: gallery ?? null,
      images: allImages,
      jobImages,
      directPhotos,
      enrichment: enrichment ?? null,
      jobDetails: jobDetails ?? null,
      jobLinkage: jobLinkage ?? null,
      stats,
      isLoading: isGalleryLoading || isImagesLoading,
      isGalleryLoading,
      isImagesLoading,
      isJobDetailsLoading,
      error: galleryError ?? null,
      approveImage,
      rejectImage,
      flagImage,
      toggleInclude,
      setCover,
      bulkApprove,
      bulkReject,
      approveGallery,
      rejectGallery,
      syncImages,
      isApproving: updateJobStatusMutation.isPending || approveGalleryMutation.isPending,
      isRejecting: updateJobStatusMutation.isPending || rejectGalleryMutation.isPending,
      isSyncing: syncImagesMutation.isPending,
      lightbox,
      openLightbox,
      closeLightbox,
      navigateLightbox,
      setLightboxIndex,
      getImageByOriginalId,
      findImageIndex,
      refetch,
    }),
    [
      galleryId,
      gallery,
      allImages,
      jobImages,
      directPhotos,
      enrichment,
      jobDetails,
      jobLinkage,
      stats,
      isGalleryLoading,
      isImagesLoading,
      isJobDetailsLoading,
      galleryError,
      approveImage,
      rejectImage,
      flagImage,
      toggleInclude,
      setCover,
      bulkApprove,
      bulkReject,
      approveGallery,
      rejectGallery,
      syncImages,
      updateJobStatusMutation.isPending,
      approveGalleryMutation.isPending,
      rejectGalleryMutation.isPending,
      syncImagesMutation.isPending,
      lightbox,
      openLightbox,
      closeLightbox,
      navigateLightbox,
      setLightboxIndex,
      getImageByOriginalId,
      findImageIndex,
      refetch,
    ]
  )

  return (
    <GalleryDetailContext.Provider value={value}>
      {children}
    </GalleryDetailContext.Provider>
  )
}
